// ═══════════════════════════════════════════════════════════════════════════════
// Helper — confirm submit for a future-dated task instance
// ═══════════════════════════════════════════════════════════════════════════════

import { useRef, useEffect, useMemo } from 'react';
import { formatSubmitConfirmDueDateLine } from '../../utils/taskRelativeDueBanner';
import { AppButton } from '../ui/AppButton';
import { AppModal } from '../ui/AppModal';

type TranslateFn = (key: string, params?: Record<string, string | number>) => string;

type TaskSubmitConfirmModalProps = {
  open: boolean;
  dueDateYmd: string;
  todayYmd: string;
  t: TranslateFn;
  title: string;
  prompt: string;
  confirmLabel: string;
  cancelLabel: string;
  submitting: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export const TaskSubmitConfirmModal = ({
  open,
  dueDateYmd,
  todayYmd,
  t,
  title,
  prompt,
  confirmLabel,
  cancelLabel,
  submitting,
  onClose,
  onConfirm,
}: TaskSubmitConfirmModalProps) => {
  const confirmRef = useRef<HTMLButtonElement>(null);

  const dueDateLine = useMemo(
    () => formatSubmitConfirmDueDateLine(todayYmd, dueDateYmd, t),
    [todayYmd, dueDateYmd, t],
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    confirmRef.current?.focus();
  }, [open]);

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={title}
      titleId="task-submit-confirm-title"
      describedById="task-submit-confirm-desc"
      hideCloseButton
      closeAriaLabel={cancelLabel}
      closeDisabled={submitting}
      backdropZClass="z-50"
    >
      <p id="task-submit-confirm-desc" className="text-sm text-gray-600 dark:text-gray-300">
        {prompt}
      </p>
      <p className="mt-2 text-sm font-medium tabular-nums text-gray-900 dark:text-gray-100">{dueDateLine}</p>
      <div className="mt-4 flex justify-end gap-2">
        <AppButton variant="secondary" onClick={onClose} disabled={submitting}>
          {cancelLabel}
        </AppButton>
        <AppButton ref={confirmRef} variant="primary" onClick={onConfirm} disabled={submitting}>
          {confirmLabel}
        </AppButton>
      </div>
    </AppModal>
  );
};
